import { unassignedCustomStops } from './customPathModel';
import type { DesignerPath, OrgPathNode, PathMode } from '../types';

export interface ReviewCheck {
  ok: boolean;
  label: string;
  soft?: boolean;
}

/**
 * Structural blockers only — never advisory items.
 *
 * The "reaches the team level (recommended)" check was removed (2026-08-27):
 * it passed whenever the endpoint was a leaf, so a two-level org satisfied it
 * automatically, and its name implied a team tier the designer never required.
 * A checklist row that is green in every real case is noise that teaches people
 * to stop reading the checklist.
 *
 * Callers render these only when `ok` is false — a passing structural check has
 * nothing to say.
 */
export function runPreLaunchChecks(
  paths: DesignerPath[],
  _orgNodes: OrgPathNode[],
  pathMode?: PathMode,
): { checks: ReviewCheck[]; allHardPass: boolean } {
  // Only structural impossibilities block launch (a path that goes nowhere).
  // Everything advisory is soft — optional fields must never gate the button
  // (owner ruling 2026-07-08).
  const custom = pathMode === 'custom';
  const unassigned = custom ? unassignedCustomStops(paths) : 0;

  const checks: ReviewCheck[] = [
    {
      // `nodes[0]` is always the anchor, so this requires the anchor plus ONE
      // stop — never two levels of org below the top. The org-chart label used
      // to read "at least 2 stops", counting the anchor as a stop; people read
      // that as a required depth and invented org levels to satisfy it. Same
      // predicate, same sentence, in both modes.
      ok: paths.every((p) => p.nodes.length >= 2),
      label: 'Every path has at least one stop below the anchor',
    },
    custom
      ? {
          // Hard, unlike the org-chart case: an unassigned org node still falls
          // back to "anyone valid there", but an unassigned custom stop names
          // no one at all and no roster can resolve it.
          ok: unassigned === 0,
          label:
            unassigned === 0
              ? 'Every stop has a person or group'
              : `${unassigned} stop${unassigned === 1 ? '' : 's'} still need a person or group`,
        }
      : null,
  ].filter((c): c is ReviewCheck => c !== null);

  const allHardPass = checks.filter((c) => !c.soft).every((c) => c.ok);
  return { checks, allHardPass };
}
